/**
 * Extracts the unit quantity from a product title using a predefined regex pattern.
 * 
 * Regex pattern: (\d+)\s*[-\s]*(?:of\s*each\s*|of\s*|per\s*|x\s*)?(?:pack|pk|pks|ct|count|cnt|piece|pcs|pc|ea|each|unit|units)(?:s?)
 * - (\d+): Captures the number (quantity).
 * - \s*[-\s]*: Handles optional whitespace and hyphens between number and unit word.
 * - (?:...): Non-capturing group for unit words.
 * - (?:s?): Optional 's' for pluralization.
 * 
 * @param title The product title string.
 * @returns The extracted quantity as a number, or 1 if no quantity is found (assuming single unit).
 */
export const extractUnitQuantity = (title: string): number => {
    if (!title) return 1;

    // Regex to find the number preceding a unit word (case-insensitive)
    const unitRegex = new RegExp("(\\d+)\\s*[-\\s]*(?:of\\s*each\\s*|of\\s*|per\\s*|x\\s*)?(?:pack|pk|pks|ct|count|cnt|piece|pcs|pc|ea|each|unit|units)(?:s?)", "i");
    
    const match = title.match(unitRegex);

    if (match && match[1]) {
        const quantity = parseInt(match[1], 10);
        // Return quantity if valid, otherwise default to 1
        return isNaN(quantity) || quantity <= 0 ? 1 : quantity;
    }

    // Default to 1 if no quantity is explicitly found
    return 1;
};

/**
 * Calculates the price per piece based on the total price and product title.
 * 
 * @param price The total price of the product.
 * @param title The product title string.
 * @returns The calculated price per piece, or null if price is invalid.
 */
export const calculatePricePerPiece = (price: number | string | null | undefined, title: string): number | null => {
    if (price === null || price === undefined || price === '') return null;

    // Safely parse the price, handling currency symbols if present
    const cleanedPrice = String(price).replace(/[$,]/g, '');
    const numericPrice = parseFloat(cleanedPrice);

    if (isNaN(numericPrice)) return null;

    const quantity = extractUnitQuantity(title);

    if (quantity <= 0) {
        // Should not happen if extractUnitQuantity works correctly, but as a safeguard
        return numericPrice;
    }

    return numericPrice / quantity;
};